import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { CollaboratorsList } from "./types";

// DELETE /api/admin/deals/collaborators/:collaboratorId
//
// Replaces /app/admin/deals/_actions.ts::removeDealCollaborator. Optimistic
// list-filter on queryKeys.deals.collaborators(hubspotDealId); rollback on
// 4xx. The hook needs both ids so the cache patch can target the right
// list — the URL only carries the collaboratorId, but the cache is keyed
// by the parent deal.

type Vars = { hubspotDealId: string; collaboratorId: string };

type Context = {
  previous: CollaboratorsList | undefined;
  key: ReturnType<typeof queryKeys.deals.collaborators>;
};

export function useRemoveDealCollaborator() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiClientError, Vars, Context>({
    mutationFn: ({ collaboratorId }) =>
      apiFetch<void>(`/admin/deals/collaborators/${collaboratorId}`, {
        method: "DELETE",
      }),
    onMutate: async ({ hubspotDealId, collaboratorId }) => {
      const key = queryKeys.deals.collaborators(hubspotDealId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CollaboratorsList>(key);
      queryClient.setQueryData<CollaboratorsList>(key, (old) => {
        if (!old) return old;
        return {
          vendor: old.vendor.filter((c) => c.id !== collaboratorId),
          partner: old.partner.filter((c) => c.id !== collaboratorId),
        };
      });
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(err.message ?? "Failed to remove collaborator");
    },
    onSettled: (_data, _err, { hubspotDealId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.deals.collaborators(hubspotDealId),
      });
      // Phase 7 sub-bucket 16 — admin drawer payload embeds the
      // collaborators array.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.admin.deals.detail(hubspotDealId),
      });
    },
    onSuccess: () => {
      toast.success("Collaborator removed");
    },
  });
}
